import React from 'react'
import { GetServerSideProps, GetServerSidePropsResult, NextPage } from 'next'

import { HomeProps, HomeServerSideProps } from 'services/api/home-page'
import { Menu, MenusApi } from '@services/api/menus'

import { HeadMetadata } from 'components/head-metadata'
import { Layout } from 'components/layout'
import {
  Courses,
  EditorialSummary,
  InfosolarStudio,
  TopNews,
  Opinion
} from '@components/home'
import { EditorialWithHighlights } from 'types/editorial'
import { convertArrayToObject } from '@utils/array-to-object'

import { AuthorsApi, Author } from '@services/api/authors'
import { getRandomItemsFromArray } from '../../utils/get-random-items-from-array'
import { shuffle } from '@utils/arrays'
import { api } from '@services/api'
import { HeaderSection } from '@components/header-section'
import { Highlight, HighlightsApi } from '@services/api/highlights'
import { Divider } from '@components/divider'
import { SponsorBanner } from '@components/sponsor-banner'
import { SimulationBanner as SimulationBannerV2 } from '@components/v2/SimulationBanner'
import { CalculatorSolarProvider } from 'context/CalculatorSolarContext'
import { useWindowDimensions } from '@hooks/use-window-dimensions'
import { AdsApi } from '@services/api/ads'
import { AdsIndexDto } from '@services/api/ads/types'
import { getSlugUrl } from '@utils/string-utils'
import { BlockCall } from 'types/block-call'
import restFetch from '@services/rest-fetch'
import { SystemsSoldSection } from '@components/home/systems-sold-section'
import { SpecialistsSection } from '@components/home/specialists-section'

interface Props extends HomeServerSideProps {
  main: Menu[]
  courses: Array<BlockCall>
  footer1: Menu[]
  footer2: Menu[]
  editorialSummaryMenus: Menu[]
  sections: { [key: string]: EditorialWithHighlights }
  authors: Author[]
  summaryHighlights: Highlight[]
  ads: AdsIndexDto[]
  simulatorDesktopImages: BlockCall[]
  simulatorMobileImages: BlockCall[]
}

const HomePage: NextPage<Props> = ({
  highlights,
  courses,
  lastArticles,
  sections,
  authors,
  main,
  footer1,
  footer2,
  editorialSummaryMenus,
  summaryHighlights,
  ads,
  simulatorDesktopImages,
  simulatorMobileImages
}) => {
  const { width } = useWindowDimensions()
  return (
    <Layout
      ad={ads.find(ad => ad.code === 'ad_sponsor_top')}
      mainMenus={main}
      footerMenus1={footer1}
      footerMenus2={footer2}
      title="Notícias e Informações sobre o Mercado de Energia Solar"
      logoHeading={true}
    >
      <HeadMetadata
        title="Portal Solar - Portal de Notícias"
        metaTitle="Portal Solar - Portal de Notícias"
        metaDescription="Acompanhe notícias sobre tecnologia, regulação, projetos, indicadores. Conheça mais sobre o site Portal Solar"
      />
      {highlights && highlights.length > 0 && (
        <>
          <TopNews
            highlights={highlights}
            lastArticles={lastArticles}
            ads={ads}
          />
          <div className="my-4">
            <Divider type="primary" orientation="horizontal" />
          </div>
        </>
      )}
      {/* SponsorBanner ad_sponsor_top removido - já é renderizado no Layout */}
      {editorialSummaryMenus.length > 0 && (
        <>
          <HeaderSection>
            <h2>Destaques dos Segmentos</h2>
          </HeaderSection>
          <EditorialSummary
            editorialNavBarItems={editorialSummaryMenus.map(
              ({ id, description, url }) => ({
                id,
                name: description,
                slug: getSlugUrl(url)
              })
            )}
            highlights={summaryHighlights}
          />
        </>
      )}
      <SponsorBanner
        className="mt-10"
        config={ads.find(ad => ad.code === 'ad_sponsor_middle')}
      />
      <div className="mt-10">
        <CalculatorSolarProvider>
          <SimulationBannerV2
            windowWidth={width}
            disableSnap={true}
            desktopImages={simulatorDesktopImages}
            mobileImages={simulatorMobileImages}
            title={<p></p>}
            headingLevel="p"
            logo={null}
          />
        </CalculatorSolarProvider>
      </div>
      {/* 
      {!sections['estudio-infosolar'] &&
        ads?.find(ad => ad.code === 'ad_estudio_infosolar') && (
          <div className="my-8">
            <SponsorBanner
              config={ads.find(ad => ad.code === 'ad_estudio_infosolar')}
            />
          </div>
        )} */}
      <div className="my-10">
        <Divider type="primary" orientation="horizontal" />
      </div>
      {sections['estudio-infosolar'] &&
        ads?.find(ad => ad.code === 'ad_estudio_infosolar') && (
          <>
            <InfosolarStudio section={sections['estudio-infosolar']} />
            {/* <div className="my-8">
              <SponsorBanner
                config={ads.find(ad => ad.code === 'ad_estudio_infosolar')}
              />
            </div> */}
            <div className="my-10">
              <Divider type="primary" orientation="horizontal" />
            </div>
          </>
        )}
      {/* INÍCIO SEÇÃO SISTEMAS VENDIDOS */}
      <section className="container mx-auto px-4 py-2 md:pb-8">
        <section id="systems-sold">
          <SystemsSoldSection data={courses} />
        </section>
      </section>
      {sections.opiniao && (
        <>
          <SpecialistsSection
            title="Opinião dos Especialistas"
            link="/noticias/editoria/opiniao"
            textLink="Ver mais artigos de opinião"
            qtyArticles={8}
            specialistsArticles={sections.opiniao.highlights}
          />
        </>
      )}
      {sections.cursos && (
        <Courses className="mb-16" section={sections.cursos} />
      )}{' '}
      <SponsorBanner
        className="mt-10"
        config={ads.find(ad => ad.code === 'ad_sponsor_bottom')}
      />
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async (): Promise<
  GetServerSidePropsResult<Props>
> => {
  const props = await HomeProps()
  const menus = await MenusApi.all()

  let randomizedAuthors: Author[]

  const {
    data: { highlights }
  } = await HighlightsApi.hightlightAuthorsIndex()

  const highlightAuthors = highlights.map(h => h.author)

  if (highlightAuthors.length === 0) {
    const {
      data: { authors }
    } = await AuthorsApi.index({ per: 4 })

    const randomAuthors = getRandomItemsFromArray(authors, 4)

    randomizedAuthors = randomAuthors.map(
      ({ id, name, avatar, path, companyName }) => ({
        id,
        name,
        avatar,
        path,
        companyName
      })
    )
  }

  const shuffledEditorialSummaryMenus = shuffle(menus.editorial_summary)
  let summaryHighlights = []

  if (
    shuffledEditorialSummaryMenus &&
    shuffledEditorialSummaryMenus.length > 0
  ) {
    const [first] = shuffledEditorialSummaryMenus
    const {
      data: { highlights: responseHighlights }
    } = await api.get<{
      highlights: Highlight[]
    }>(`/editorials/${getSlugUrl(first.url)}/highlights`)

    summaryHighlights = responseHighlights
  }

  // prepara section
  const filterSection = ['estudio-infosolar', 'opiniao', 'cursos']
  const sectionsAsEditorials = props.editorials.filter(
    editorial =>
      filterSection.includes(editorial.slug) &&
      editorial?.highlights?.length > 0
  )
  const sections: { [key: string]: EditorialWithHighlights } =
    convertArrayToObject(sectionsAsEditorials, 'slug')

  // Busca 8 highlights para a seção de opinião (se ela existir)
  if (sections['opiniao']) {
    try {
      const {
        data: { highlights: opiniaoHighlights }
      } = await api.get<{
        highlights: Highlight[]
      }>(`/editorials/opiniao/highlights?per=8`)

      sections['opiniao'].highlights = opiniaoHighlights
    } catch (error) {
      console.error('Erro ao buscar highlights de opinião:', error)
    }
  }

  const { blockCalls: courses } = await restFetch<{
    blockCalls: BlockCall[]
  }>('/block_calls?block_type=home_solar_courses')
  const {
    data: { ads }
  } = await AdsApi.index({
    codes: [
      'ad_sponsor_top',
      'ad_sponsor_middle',
      'ad_sponsor_bottom',
      'ad_sponsor_news_top_right'
    ]
  })

  // Buscar imagens do simulador
  const simulatorDesktopResponse = await restFetch<{
    blockCalls: BlockCall[]
  }>('/block_calls?block_type=simulator')
  const simulatorDesktopImages = simulatorDesktopResponse?.blockCalls || []

  const simulatorMobileResponse = await restFetch<{
    blockCalls: BlockCall[]
  }>('/block_calls?block_type=simulator_image_mobile')
  const simulatorMobileImages = simulatorMobileResponse?.blockCalls || []

  return {
    props: {
      ...props,
      ...menus,
      sections,
      authors:
        highlightAuthors.length === 0 ? randomizedAuthors : highlightAuthors,
      editorialSummaryMenus: shuffledEditorialSummaryMenus,
      summaryHighlights,
      ads: ads || null,
      courses,
      simulatorDesktopImages,
      simulatorMobileImages
    }
  }
}

export default HomePage
